import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { reviewService } from '../../../services/reviewService';
import Icon from '../../../components/AppIcon'; // Assuming Icon is still needed
import Button from '../../../components/ui/Button'; // Assuming Button is still needed
import EditReviewModal from './EditReviewModal'; // Import the new modal

const MyReviewsTab = () => {
    const { user } = useAuth();
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showEditModal, setShowEditModal] = useState(false); // State for modal visibility
    const [editingReview, setEditingReview] = useState(null); // State to hold the review being edited

    useEffect(() => {
        const fetchReviews = async () => {
            if (!user?.id) {
                setError('User not authenticated.');
                setLoading(false);
                return;
            }
            try {
                setLoading(true);
                const data = await reviewService.getReviewsForUser(user.id);
                setReviews(data);
                setError('');
            } catch (err) {
                console.error('Error fetching user reviews:', err);
                setError(err.message || 'Failed to fetch user reviews.');
            } finally {
                setLoading(false);
            }
        };

        fetchReviews();
    }, [user?.id]);

    const handleEditReview = (reviewToEdit) => {
        console.log('handleEditReview called with:', reviewToEdit);
        setEditingReview(reviewToEdit);
        setShowEditModal(true);
    };

    const handleDeleteReview = async (reviewId, reviewType) => {
        if (window.confirm('Are you sure you want to delete this review?')) {
            try {
                await reviewService.deleteReview(reviewId, reviewType); // Pass reviewType
                setReviews(prevReviews => prevReviews.filter(review => review.id !== reviewId));
            } catch (err) {
                console.error('Error deleting review:', err);
                setError('Failed to delete review.');
            }
        }
    };

    const handleUpdateReview = async (reviewId, reviewType, updatedData) => {
        try {
            setLoading(true); // Indicate that an update is in progress
            const updatedReview = await reviewService.updateReview(reviewId, reviewType, updatedData);
            setReviews(prevReviews => prevReviews.map(review => 
                review.id === reviewId ? { ...review, ...updatedReview } : review
            ));
            setError('');
        } catch (err) {
            console.error('Error updating review:', err);
            setError(err.message || 'Failed to update review.');
        } finally {
            setLoading(false);
        }
    };

    const productReviews = reviews.filter(review => review.reviewType === 'Product');
    const componentReviews = reviews.filter(review => review.reviewType === 'Component');
    const sellerReviews = reviews.filter(review => review.reviewType === 'Seller');

    if (loading && !editingReview) { // Only show full loading screen if not in edit modal
        return (
            <div className="flex justify-center items-center h-48">
                <Icon name="Loader" className="animate-spin text-primary h-8 w-8" />
                <p className="ml-2 text-muted-foreground">Loading reviews...</p>
            </div>
        );
    }

    if (error && !editingReview) { // Only show full error screen if not in edit modal
        return (
            <div className="text-center p-4 text-destructive-foreground bg-destructive/10 border border-destructive rounded-lg">
                <Icon name="AlertCircle" className="inline mr-2" />
                {error}
            </div>
        );
    }

    // Helper to render individual review card content
    const renderReviewCard = (review, reviewType) => (
        <div key={review.id} className="bg-card p-4 rounded-lg border">
            <div className="flex items-center mb-2">
                <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                        <svg key={i} className={`w-5 h-5 ${i < review.rating ? 'text-yellow-400' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                        </svg>
                    ))}
                </div>
                <h4 className="font-bold ml-3 text-foreground">{review.reviewedItemName}</h4>
            </div>
            <p className="text-muted-foreground">{review.review}</p>
            <p className="text-sm text-muted-foreground mt-2">Reviewed on {new Date(review.createdAt).toLocaleDateString()}</p>
            <div className="flex justify-end space-x-2 mt-4">
                <Button
                    variant="outline"
                    size="sm"
                    iconName="Edit"
                    onClick={() => handleEditReview(review)} // Pass the full review object
                >
                    Edit
                </Button>
                <Button
                    variant="destructive"
                    size="sm"
                    iconName="Trash2"
                    onClick={() => handleDeleteReview(review.id, reviewType)}
                >
                    Delete
                </Button>
            </div>
        </div>
    );

    if (reviews.length === 0) {
        return (
            <div className="text-center p-4">
                <Icon name="MessageSquare" className="mx-auto h-12 w-12 text-muted-foreground" />
                <p className="mt-2 text-lg font-semibold text-foreground">No reviews yet</p>
                <p className="text-muted-foreground">You haven't submitted any reviews.</p>
                {/* Potentially add a link to browse products to review */}
            </div>
        );
    }

    return (
        <div className="space-y-8">
            <h2 className="text-2xl font-bold text-foreground mb-6">My Reviews</h2>

            <div className="space-y-8">
                <div>
                    <h3 className="text-lg font-medium text-foreground mb-4">Product Reviews</h3>
                    <div className="space-y-4">
                        {productReviews.length > 0 ? (
                            productReviews.map(review => renderReviewCard(review, 'product'))
                        ) : (
                            <p className="text-muted-foreground">No product reviews.</p>
                        )}
                    </div>
                </div>

                <div>
                    <h3 className="text-lg font-medium text-foreground mb-4">Component Reviews</h3>
                    <div className="space-y-4">
                        {componentReviews.length > 0 ? (
                            componentReviews.map(review => renderReviewCard(review, 'component'))
                        ) : (
                            <p className="text-muted-foreground">No component reviews.</p>
                        )}
                    </div>
                </div>

                <div>
                    <h3 className="text-lg font-medium text-foreground mb-4">Seller Reviews</h3>
                    <div className="space-y-4">
                        {sellerReviews.length > 0 ? (
                            sellerReviews.map(review => renderReviewCard(review, 'seller'))
                        ) : (
                            <p className="text-muted-foreground">No seller reviews.</p>
                        )}
                    </div>
                </div>
            </div>
            {editingReview && (
                <EditReviewModal
                    isOpen={showEditModal}
                    onClose={() => setShowEditModal(false)}
                    onSubmit={handleUpdateReview}
                    initialData={editingReview}
                    loading={loading}
                />
            )}
        </div>
    );
};

export default MyReviewsTab;